import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
} from '@angular/core';
import { DashboardCardComponent } from '../../../../shared/components/dashboard-card/dashboard-card.component';
import { ExploreCardComponent } from '../../../../shared/components/explore-card/explore-card.component';
import { Router } from '@angular/router';
import { EXTERNAL_URLS } from '../../../../shared/config/external-urls';
import { NotificationService } from '../../../../shared/services/notification.service';
import config from '../../../../../config';

interface DashboardCard {
  title: string;
  description: string;
  buttonText: string;
  icon: string;
  route: string;
}

interface ExploreCard {
  title: string;
  description: string;
  icon: string;
  link: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [DashboardCardComponent, ExploreCardComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent {
  private readonly router = inject(Router);
  readonly notificationService = inject(NotificationService);

  private static readonly MY_TRANSLATION_JOBS_DEFAULT_TEXT =
    'View, track and manage all your translation requests in one place.';
  private static readonly QUOTES_TO_BE_REVIEWED_DEFAULT_TEXT =
    'Compare vendor quotes and take the next step on your requests.';

  // same behavior as the nav items, but for the dashboard cards on the home page
  private static readonly isProd = config.environment_name === 'prod';

  private static readonly DEEPL_FREE_TEXT_URL = HomeComponent.isProd
    ? 'https://astrazeneca.app.deepl.com/translator'
    : 'https://auth.deepl.com/login';

  private static readonly DEEPL_FILES_URL = HomeComponent.isProd
    ? 'https://astrazeneca.app.deepl.com/translator/files'
    : 'https://auth.deepl.com/login';

  dashboardCards: DashboardCard[] = [
    {
      title: 'Request a Vendor Quote',
      description:
        'Submit a request to receive a translation quote from approved vendors.',
      buttonText: 'New Request',
      icon: 'article_person',
      route: 'vendor-translation',
    },
    {
      title: 'My Translation Jobs',
      description:
        'View, track and manage all your translation requests in one place.',
      buttonText: 'Translation Manager',
      icon: 'library_books',
      route: '/translation-manager',
    },
    {
      title: 'Quotes to Review',
      description:
        'Compare vendor quotes and take the next step on your requests.',
      buttonText: 'Quote Review',
      icon: 'receipt_long',
      route: '/translation-manager?prefilter=quotes',
    },
  ];

  exploreCards: ExploreCard[] = [
    {
      title: 'DeepL Translator',
      description:
        'Quickly translate text using DeepL. Just copy and paste your content.',
      icon: 'translate',
      link: HomeComponent.DEEPL_FREE_TEXT_URL,
    },
    {
      title: 'Translate Files',
      description:
        'Upload documents for instant, machine-generated translations (not human reviewed).',
      icon: 'description',
      link: HomeComponent.DEEPL_FILES_URL,
    },
  ];

  readonly cardNotificationDots = computed<Record<string, boolean>>(() => ({
    'My Translation Jobs':
      this.notificationService.hasCompletedDocsNotification() ||
      this.notificationService.hasNewMessagesNotification(),
    'Quotes to Review':
      this.notificationService.hasAwaitingVendorNotification(),
  }));

  getCardDescription(card: DashboardCard): string {
    if (card.title === 'My Translation Jobs') {
      return this.getMyTranslationJobsDescription();
    }

    if (card.title === 'Quotes to Review') {
      return this.getQuotesToBeReviewedDescription();
    }

    return card.description;
  }

  private getMyTranslationJobsDescription(): string {
    const completedJobsCount = this.notificationService.completedJobs().length;

    if (completedJobsCount === 0) {
      return HomeComponent.MY_TRANSLATION_JOBS_DEFAULT_TEXT;
    }

    const verb = completedJobsCount === 1 ? 'is' : 'are';
    const noun = completedJobsCount === 1 ? 'job' : 'jobs';

    return `There ${verb} ${completedJobsCount} recently completed translated ${noun} ready for your review.`;
  }

  private getQuotesToBeReviewedDescription(): string {
    const awaitingVendorCount =
      this.notificationService.awaitingVendorJobs().length;

    if (awaitingVendorCount === 0) {
      return HomeComponent.QUOTES_TO_BE_REVIEWED_DEFAULT_TEXT;
    }

    if (awaitingVendorCount === 1) {
      return 'The quote for 1 job is ready for your review.';
    }

    return `The quotes for ${awaitingVendorCount} jobs are ready for your review.`;
  }

  navigateTo(route: string): void {
    if (route === '/translation-manager?prefilter=quotes') {
      this.router.navigate(['/translation-manager'], {
        state: {
          prefilterQuotes: true,
          selectedFilters: ['Quotes Pending', 'Quotes to be Reviewed'],
        },
      });
      return;
    }

    if (route.startsWith('http')) {
      window.open(route, '_blank', 'noopener,noreferrer');
    } else {
      this.router.navigate([route]);
    }
  }
}
